import React from 'react'
import { Employer } from '../../api/organizations'
import SidebarLayout from '../../components/layout/sidebar-layout'
import {
  CalendarIcon,
  EmployerIcon,
  EmployerServiceIcon,
  InterimAgencyIcon,
  JobTitleIcon,
  TerminalIcon,
  WorkerCounterIcon,
  WorkerIcon,
  WorkPeriodEventIcon,
  WorkPeriodTagIcon,
} from '../../components/icons'
import useStore from '../../store'
import { ClockIcon, UserGroupIcon } from '@heroicons/react/outline'
import { CurrencyEuroIcon } from '@heroicons/react/outline'
import { usePendingEmployerInvitationsCount } from '../../hooks/use-pending-employer-invitations-count'
import { useOrganizationQuery } from '../../queries/organizations'
import { isAuthorizedForOption } from '../../components/protected/organization-option-protected'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { isAuthorizedFor } from '../../components/protected/protected'

export interface OutletContext {
  employer: Employer
}

interface EmployerSettingsWrapperProps {
  mode?: 'currentOrganization' | 'urlId'
}

const EmployerSettingsWrapper: React.FC<EmployerSettingsWrapperProps> = ({
  mode = 'currentOrganization',
}) => {
  const { id } = useParams()
  const location = useLocation()
  const currentEmployer = useStore(state => state.session.currentOrganization as Employer)
  const { data: employerData } = useOrganizationQuery<Employer>(
    mode === 'currentOrganization' ? currentEmployer._id : id,
    'employer',
  )
  const pendingEmployerInvitationsCount = usePendingEmployerInvitationsCount()

  const employer = employerData
  const menu = {
    missionsAndWorkPeriods: {
      title: 'Missions & Journées de Travail',
      sections: (() => {
        const sections: Array<{
          path: string
          label: string
          icon: React.ComponentType
        }> = []
        if (isAuthorizedFor(['employerMember'])) {
          sections.push(
            {
              path: 'services',
              label: 'Affectations',
              icon: EmployerServiceIcon,
            },
            {
              path: 'work-period-tags',
              label: 'Catégories',
              icon: WorkPeriodTagIcon,
            },
            {
              path: 'job-titles',
              label: 'Métiers',
              icon: JobTitleIcon,
            },
            {
              path: 'clocking-rules',
              label: 'Règles de Pointage',
              icon: ClockIcon,
            },
          )
        }
        sections.push({
          path: 'work-period-events',
          label: 'Évènements',
          icon: WorkPeriodEventIcon,
        })
        return sections
      })(),
    },
    finances: {
      title: 'Finances',
      sections: (() => {
        const sections: Array<{
          path: string
          label: string
          icon: React.ComponentType
        }> = []
        if (isAuthorizedFor(['employerMember'])) {
          sections.push({
            path: 'labor-costs-coefficients',
            label: "Coefficients Agences d'Intérim",
            icon: () => <>%</>,
          })
        }
        sections.push({
          path: 'employer-rules',
          label: 'Règles employeur',
          icon: CurrencyEuroIcon as React.ComponentType,
        })
        if (isAuthorizedFor(['superAdmin'])) {
          sections.push({
            path: 'worker-counters',
            label: 'Compteurs',
            icon: WorkerCounterIcon as React.ComponentType,
          })
        }
        return sections
      })(),
    },
    collaboration: {
      title: 'Collaboration',
      sections: (() => {
        const sections: Array<{
          path: string
          label: string
          icon: React.ComponentType
          badgeCount?: number
        }> = []
        if (isAuthorizedFor(['employerMember'])) {
          sections.push(
            {
              path: 'partners-agencies',
              label: 'Agences Partenaires',
              icon: InterimAgencyIcon,
              badgeCount: pendingEmployerInvitationsCount,
            },
            {
              path: 'members',
              label: 'Gestionnaires de mon entreprise',
              icon: UserGroupIcon,
            },
          )
        }
        return sections
      })(),
    },
    settings: {
      title: 'Paramètres',
      sections: [
        {
          path: 'terminals',
          label: 'Bornes de Pointage',
          icon: TerminalIcon,
        },
        {
          path: 'info',
          label: "Informations de l'Entreprise",
          icon: EmployerIcon,
        },
        ...(isAuthorizedFor(['superAdmin'])
          ? [
              {
                path: 'special-periods',
                label: 'Périodes Spéciales',
                icon: CalendarIcon as React.ComponentType,
              },
            ]
          : []),
      ],
    },
  }

  if (
    isAuthorizedFor(['employerMember']) &&
    isAuthorizedForOption([{ organizationType: 'employer', option: 'biPart' }])
  ) {
    menu.missionsAndWorkPeriods.sections.push({
      path: 'workers',
      label: 'Salariés',
      icon: WorkerIcon,
    })
    menu.finances.sections.splice(1, 0, {
      path: 'worker-counters',
      label: 'Compteurs',
      icon: WorkerCounterIcon,
    })
  }

  if (!employer) {
    return <></>
  }

  if (location.pathname === '/settings') {
    return <Navigate to="/settings/info" replace />
  }

  if (mode === 'urlId' && location.pathname === `/employers/${id}`) {
    return <Navigate to={`/employers/${id}/info`} replace />
  }

  const basePath = mode === 'currentOrganization' ? '/settings' : `/employers/${id}`

  return (
    <SidebarLayout<OutletContext>
      basePath={basePath}
      menu={menu}
      outletContext={{ employer }}
      menuTitle={isAuthorizedFor(['superAdmin']) ? `${employer?.name} (EU)` : undefined}
    />
  )
}

export default EmployerSettingsWrapper
